var __makeTemplateObject = (this && this.__makeTemplateObject) || function (cooked, raw) {
    if (Object.defineProperty) { Object.defineProperty(cooked, "raw", { value: raw }); } else { cooked.raw = raw; }
    return cooked;
};
var __assign = (this && this.__assign) || function () {
    __assign = Object.assign || function(t) {
        for (var s, i = 1, n = arguments.length; i < n; i++) {
            s = arguments[i];
            for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p))
                t[p] = s[p];
        }
        return t;
    };
    return __assign.apply(this, arguments);
};
var __rest = (this && this.__rest) || function (s, e) {
    var t = {};
    for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p) && e.indexOf(p) < 0)
        t[p] = s[p];
    if (s != null && typeof Object.getOwnPropertySymbols === "function")
        for (var i = 0, p = Object.getOwnPropertySymbols(s); i < p.length; i++) {
            if (e.indexOf(p[i]) < 0 && Object.prototype.propertyIsEnumerable.call(s, p[i]))
                t[p[i]] = s[p[i]];
        }
    return t;
};
import React, { useState } from 'react';
import PropTypes from 'prop-types';
import styled from 'styled-components';
import { Check, Success, SuccessOutline, Visibility, VisibilityOff } from 'pcln-icons';
import { Badge, Flex, IconButton, IconField, Input, Label, ProgressBar } from '..';
import { Text } from '../Text';
var NoWrapText = styled(Text)(templateObject_1 || (templateObject_1 = __makeTemplateObject(["\n  white-space: nowrap;\n"], ["\n  white-space: nowrap;\n"])));
var maxProgressBarLength = 4;
var propTypes = {
    id: PropTypes.string,
    isValid: PropTypes.bool,
    label: PropTypes.string,
    hasProgressBar: PropTypes.bool,
    progressBarSteps: PropTypes.arrayOf(PropTypes.shape({ color: PropTypes.string, text: PropTypes.string })),
    progressBarDefaultStep: PropTypes.number,
    regexChecks: PropTypes.arrayOf(PropTypes.shape({ label: PropTypes.string, regex: PropTypes.regex })),
    value: PropTypes.string,
    onChange: PropTypes.func,
    autoComplete: PropTypes.string,
};
var PasswordInput = function (_a) {
    var id = _a.id, isValid = _a.isValid, label = _a.label, hasProgressBar = _a.hasProgressBar, progressBarSteps = _a.progressBarSteps, regexChecks = _a.regexChecks, value = _a.value, onChange = _a.onChange, autoComplete = _a.autoComplete, props = __rest(_a, ["id", "isValid", "label", "hasProgressBar", "progressBarSteps", "regexChecks", "value", "onChange", "autoComplete"]);
    var _b = useState(false), showPassword = _b[0], setShowPassword = _b[1];
    var _c = useState([]), passedChecks = _c[0], setPassedChecks = _c[1];
    function changeVisibility() {
        return setShowPassword(!showPassword);
    }
    function handleChange(event) {
        var currentValue = event.target.value;
        var passedChecks = [];
        if (hasProgressBar) {
            regexChecks.reduce(function (acc, element, index) { return element.regex.test(currentValue) && passedChecks.push(index); }, passedChecks);
            setPassedChecks(passedChecks);
        }
        var lengthsMatch = !hasProgressBar || passedChecks.length === regexChecks.length;
        if (onChange) {
            onChange({ isValid: lengthsMatch, value: currentValue });
        }
    }
    var inputType = showPassword ? 'text' : 'password';
    var VisibilityIcon = showPassword ? VisibilityOff : Visibility;
    var strengthLevel = Math.max(Math.round((maxProgressBarLength / regexChecks.length) * passedChecks.length), 0);
    var currentStep = progressBarSteps[strengthLevel - 1];
    var showCheckIcon = isValid || (hasProgressBar && strengthLevel === maxProgressBarLength);
    return (React.createElement(Flex, __assign({ flexDirection: 'column' }, props),
        React.createElement(Label, { htmlFor: id, fontSize: 0, mb: 2 }, label),
        React.createElement(IconField, null,
            React.createElement(Input, { id: id, type: inputType, value: value, onChange: handleChange, "data-testid": 'input-field', autoComplete: autoComplete }),
            showCheckIcon && React.createElement(Check, { color: 'secondary', "data-testid": 'check-mark-icon' }),
            React.createElement(IconButton, { title: 'visibility-button', icon: React.createElement(VisibilityIcon, { color: 'text.light' }), onClick: changeVisibility })),
        hasProgressBar && (React.createElement(Flex, { flexDirection: 'column' },
            React.createElement(Flex, { alignItems: 'center', my: 2 },
                React.createElement(NoWrapText, { fontSize: 0, color: 'text.light', mr: 2 },
                    "Password Strength:",
                    ' '),
                currentStep && (React.createElement(Badge, { color: currentStep.color, size: 'small' }, currentStep.text))),
            React.createElement(ProgressBar, { steps: progressBarSteps, currentStep: strengthLevel }),
            React.createElement(Flex, { flexDirection: ['column', null, 'row'], mt: 2, mb: [2, null, 0] },
                React.createElement(NoWrapText, { fontSize: 0, color: 'text.light', mr: 3 }, "Must contain:"),
                React.createElement(Flex, { flexWrap: 'wrap' }, regexChecks.map(function (check, index) {
                    var didPass = passedChecks.findIndex(function (value) { return index === value; }) !== -1;
                    var Icon = didPass ? Success : SuccessOutline;
                    var color = didPass ? 'success' : 'text.light';
                    return (React.createElement(Flex, { alignItems: 'center', mt: [2, null, 0], mb: [0, null, 2], key: 'regex-label-' + index },
                        React.createElement(Icon, { size: '16px', color: color, mr: 1, "data-testid": Icon === Success ? 'check-icon-on' : 'check-icon-off' }),
                        React.createElement(NoWrapText, { fontSize: 0, color: color, mr: 3 }, check.label)));
                })))))));
};
PasswordInput.defaultProps = {
    hasProgressBar: false,
    progressBarSteps: [
        { color: 'warning', text: 'WEAK' },
        { color: 'caution', text: 'FAIR' },
        { color: 'primary', text: 'GOOD' },
        { color: 'success', text: 'STRONG' },
    ],
    progressBarDefaultStep: 1,
    regexChecks: [
        { label: '1 Uppercase Letter', regex: /(?=.*[A-Z])/ },
        { label: '1 Lowercase Letter', regex: /(?=.*[a-z])/ },
        { label: '1 Number', regex: /(?=.*[0-9])/ },
        { label: '1 Special Character', regex: /(?=.*[!@#$%^&*()])/ },
        { label: 'at least 12 Characters', regex: /.{12,}/ },
    ],
};
PasswordInput.propTypes = propTypes;
export default PasswordInput;
var templateObject_1;
//# sourceMappingURL=PasswordInput.js.map